import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ItemId from "src/views/shopping/Orders/ItemId";

const FONT_SIZES = {
  header: 18,
  primary: 16,
  secondary: 15,
  total: 18,
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildLocationDetailUrl = (warehouseId, warehouseLocationId) => {
  if (!warehouseId || !warehouseLocationId) return null;
  return `/warehouse/${warehouseId}/table?locId=${warehouseLocationId}&locType=WarehouseLocation`;
};

const buildLocationDimensionsRows = (inventoryItems = []) =>
  inventoryItems
    .map((item) => {
      const locations = (item?.rackLocations || []).flatMap((rackLocation) => {
        const warehouseId = rackLocation?.warehouse?.id;

        return (rackLocation?.warehouse_locations || []).map((warehouseLocation) => ({
          id: warehouseLocation?.id,
          locationName: warehouseLocation?.location_name || "Unknown location",
          squareFootage: toNumber(warehouseLocation?.sq_ft_of_slot),
          detailUrl: buildLocationDetailUrl(warehouseId, warehouseLocation?.id),
        }));
      });

      const totalSquareFootage = locations.reduce(
        (sum, location) => sum + location.squareFootage,
        0
      );

      if (!locations.length && totalSquareFootage <= 0) return null;

      return {
        id: item?.id,
        asset: item?.asset,
        assetDescription: item?.asset?.description,
        locations,
        totalSquareFootage,
      };
    })
    .filter(Boolean);

const formatSquareFootageList = (locations = []) =>
  locations.map((location) => `${location.squareFootage.toLocaleString()} sq ft`).join(" + ");

export default function LocationDimensionsBreakdown({ inventoryItems = [] }) {
  const rows = useMemo(() => buildLocationDimensionsRows(inventoryItems), [inventoryItems]);
  const totalSquareFootage = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.totalSquareFootage || 0), 0),
    [rows]
  );

  return (
    <Box mt={4} mb={3}>
      <Box
        display="grid"
        gridTemplateColumns="minmax(0, 1.3fr) minmax(0, 2fr)"
        gap={2}
        px={1}
        pb={1}
        borderBottom="1px solid"
        borderColor="divider"
        textAlign="center"
      >
        <Typography
          color="text.secondary"
          align="center"
          fontSize={FONT_SIZES.header}
          fontWeight={500}
        >
          Item
        </Typography>
        <Typography
          color="text.secondary"
          align="center"
          fontSize={FONT_SIZES.header}
          fontWeight={500}
        >
          Location Math
        </Typography>
      </Box>

      {rows.length > 0 ? (
        rows.map((row) => (
          <Box
            key={row.id}
            py={2}
            px={1}
            borderBottom="1px solid"
            borderColor="divider"
          >
            <Box
              display="grid"
              gridTemplateColumns="minmax(0, 1.3fr) minmax(0, 2fr)"
              gap={2}
              alignItems="start"
              textAlign="center"
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                {row.asset?.itemId ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    sx={{ fontSize: FONT_SIZES.primary, fontWeight: 500, lineHeight: 1.35 }}
                  >
                    <ItemId asset={row.asset} />
                  </Box>
                ) : (
                  <Typography
                    align="center"
                    fontSize={FONT_SIZES.primary}
                    fontWeight={500}
                    lineHeight={1.35}
                  >
                    {`Inventory Item ${row.id}`}
                  </Typography>
                )}
                {row.assetDescription && (
                  <Typography
                    color="text.secondary"
                    align="center"
                    fontSize={FONT_SIZES.secondary}
                    lineHeight={1.4}
                  >
                    {row.assetDescription}
                  </Typography>
                )}
              </Box>

              <Box display="flex" flexDirection="column" alignItems="center">
                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                  {row.locations.map((location, index) => (
                    <Box key={`${row.id}-${location.id}-${index}`} display="flex" flexDirection="column" alignItems="center">
                      {index > 0 && (
                        <Typography
                          color="text.secondary"
                          fontSize={FONT_SIZES.primary}
                          fontWeight={500}
                          lineHeight={1.2}
                        >
                          +
                        </Typography>
                      )}
                      <Button
                        variant="text"
                        size="small"
                        color="primary"
                        component="a"
                        href={location.detailUrl || undefined}
                        target="_blank"
                        rel="noreferrer"
                        disabled={!location.detailUrl}
                        sx={{
                          minWidth: "auto",
                          px: 0,
                          fontSize: FONT_SIZES.primary,
                          fontWeight: 400,
                          lineHeight: 1.35,
                          textTransform: "none",
                        }}
                      >
                        {location.locationName}
                      </Button>
                    </Box>
                  ))}
                </Box>
                {row.locations.length === 1 ? (
                  <Typography
                    color="text.secondary"
                    mt={0.5}
                    align="center"
                    fontSize={FONT_SIZES.secondary}
                    lineHeight={1.4}
                  >
                    {formatSquareFootageList(row.locations)}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      color="text.secondary"
                      mt={0.75}
                      align="center"
                      fontSize={FONT_SIZES.secondary}
                      lineHeight={1.4}
                    >
                      {formatSquareFootageList(row.locations)}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      align="center"
                      fontSize={FONT_SIZES.secondary}
                      lineHeight={1.4}
                    >
                      Total: {row.totalSquareFootage.toLocaleString()} sq ft
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        <Typography
          color="text.secondary"
          align="center"
          fontSize={FONT_SIZES.secondary}
          lineHeight={1.4}
          sx={{ py: 4 }}
        >
          No picked warehouse locations were found for this order.
        </Typography>
      )}

      <Box display="flex" justifyContent="space-between" mt={2} px={1}>
        <Typography fontSize={FONT_SIZES.total} fontWeight={500} lineHeight={1.3}>
          Total Square Footage
        </Typography>
        <Typography fontSize={FONT_SIZES.total} fontWeight={500} lineHeight={1.3}>
          {totalSquareFootage.toLocaleString()} sq ft
        </Typography>
      </Box>
    </Box>
  );
}
